// regenerator runtime polyfills
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar"
import { Stack } from "@fluentui/react/lib/Stack"
import { IProcessedStyleSet } from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import "core-js/stable"
import { merge } from "lodash"
import React, { ForwardedRef, useEffect, useState } from "react"
import "regenerator-runtime/runtime"

import * as cServicesImport from "@encoway/c-services-js-client"
import { ConfigurationService } from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"
import * as reactConfiguratorImport from "@encoway/react-configurator"
import { Configurator } from "@encoway/react-configurator"
import { Http } from "@encoway/sales-showroom-auth"

import {
    IAppSettings,
    loadTranslations,
    mergeDefaultSettings,
    registerAliases
} from "../../appUtil"
import {
    BorderLayoutContent,
    ComponentName,
    Events,
    ExternalMessages,
    HeaderSettings,
    Orientation
} from "../../constants"
import { postMessage } from "../../messageUtil"
import { IVisualEditor } from "../../services/visualization/visualizationService.types"
import { BorderLayout as BorderLayoutComponent } from "../BorderLayout/BorderLayout"
import { BorderLayoutPosition } from "../BorderLayout/BorderLayout.types"
import { BusyOverlayHandle } from "../BusyOverlay/BusyOverlay.types"
import {
    isVisualizationEnabled,
    VisualizationWrapper
} from "../VisualizationWrapper/VisualizationWrapper"
import { CUIAppStyles } from "./CUIApp.styles"
import {
    BorderLayoutDefinition,
    ICUIAppProps,
    ICUIAppState,
    ICUIAppStyles
} from "./CUIApp.types"

/**
 * Loads the configuration server connection.
 * @param {Object} appState - The application state (settings and vis, configurationService... everything that needs to be loaded).
 * @param {Object} cServices - Reference to the configuration service api.
 */
async function loadConfiguration(
    appState: ICUIAppState,
    cServices: typeof cServicesImport
) {
    const configService = appState.configurationService
    const configurationSettings = appState.settings.configuration.settings
    const http = Http.create(appState.settings.configuration)
    const configurationService = await cServices.ConfigurationService.create(
        http,
        appState.settings.configuration?.baseUrl || "",
        appState.settings.configuration?.options,
        L10n.currentFullLocale
    )
    configService?.disposeHeartbeat && configService.disposeHeartbeat()
    configurationService.settings(configurationSettings)
    return {
        ...appState,
        configurationService
    }
}

/**
 * The default layout for the root BorderLayout component.
 */
const defaultLayout = [
    {
        name: BorderLayoutContent.Header,
        position: Orientation.North
    },
    {
        name: BorderLayoutContent.Configuration,
        position: Orientation.Center
    },
    {
        name: BorderLayoutContent.VisualEditor,
        position: Orientation.West,
        resizable: true
    }
]

const defaultBorderStyles = {
    west: { width: "40%" },
    center: { overflow: "hidden" }
}

const prepareLayout = (
    layout: BorderLayoutPosition[],
    state: ICUIAppState,
    showVisualization: boolean,
    hiddenViews: string[],
    resizableViews: string[]
) => {
    if (!showVisualization) {
        const position = layout.find(
            (l) => l.name === BorderLayoutContent.VisualEditor
        )?.position
        if (position) {
            hiddenViews.push(position)
        }
    }
    if (!state.visualizationEnabled) {
        layout = layout.filter(
            (l) => l.name !== BorderLayoutContent.VisualEditor
        )
    }

    layout
        .filter((l) => l.resizable)
        .forEach((l) => resizableViews.push(l.position))
    return layout
}

const stopHeartBeat = (cService?: ConfigurationService) => {
    cService?.disposeHeartbeat && cService.disposeHeartbeat()
}

/**
 * CUIApp renders a default configurator (optionally including the visual editor).
 *
 * @visibleName CUIApp
 */
const ICUIApp = (
    props: ICUIAppProps,
    visualEditorForwardRef: ForwardedRef<IVisualEditor>
) => {
    const [state, setState] = useState({
        initialized: false,
        settings: {}
    } as ICUIAppState)
    const [settingsChanged, setSettingsChanged] = useState(false)
    const [showVisualization, setShowVisualization] = useState(
        HeaderSettings.get(HeaderSettings.ShowVisualization, true)
    )
    const [showPrices, setShowPrices] = useState(
        HeaderSettings.get(
            HeaderSettings.ShowPrices,
            state.settings.configuration?.settings?.pricingMappingType !==
                "NONE"
        )
    )

    const busyOverlay = React.createRef<BusyOverlayHandle>()
    const configuratorRef = React.createRef<Configurator>()
    const confServiceRef = React.useRef<ConfigurationService | undefined>()

    if (
        confServiceRef.current &&
        state.configurationService !== confServiceRef.current
    ) {
        stopHeartBeat(confServiceRef.current)
    }
    if (state.configurationService) {
        confServiceRef.current = state.configurationService
    }

    let layout = props.layout || (defaultLayout as BorderLayoutDefinition)
    const borderStyles = merge(defaultBorderStyles, props.borderStyles || {})
    const { styles, theme } = props
    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<ICUIAppStyles>

    const reactConfigurator = props.reactConfigurator || reactConfiguratorImport
    const cServices = props.cServices || cServicesImport

    const onPending = (pending: boolean) => {
        postMessage(
            pending
                ? ExternalMessages.ExternalProcessingStarted
                : ExternalMessages.ExternalProcessingFinished,
            props.eventBus
        )
        busyOverlay.current?.setVisible(pending)
    }
    const onSavePoint = (valid: boolean) => {
        if (busyOverlay.current?.isVisible() && !valid) {
            postMessage(
                ExternalMessages.ExternalUserInteractionRequired,
                props.eventBus
            )
        }
    }

    useEffect(() => {
        if (state?.initialized) {
            setSettingsChanged(true)
        }
    }, [props.settings])

    useEffect(() => {
        return () => stopHeartBeat(confServiceRef.current)
    }, [])

    // update local storage on change of global settings
    useEffect(() => {
        if (state.initialized) {
            const settings = { ...state.settings.configuration.settings }
            if (showPrices) {
                if (settings.mappingOptions.pricingMappingType === "NONE") {
                    settings.mappingOptions = {
                        ...settings.mappingOptions,
                        pricingMappingType: "TOTAL"
                    }
                }
                state.configurationService.settings(settings)
            } else {
                settings.mappingOptions = {
                    ...settings.mappingOptions,
                    pricingMappingType: "NONE"
                }
                state.configurationService.settings(settings)
            }
            configuratorRef.current?.updateState()
            window.localStorage.setItem(
                HeaderSettings.ShowPrices,
                String(showPrices)
            )
        }
    }, [showPrices])
    useEffect(() => {
        if (state.visualizationEnabled) {
            window.localStorage.setItem(
                HeaderSettings.ShowVisualization,
                String(showVisualization)
            )
        }
    }, [showVisualization])

    // reload everything on start
    useEffect(() => {
        const unsubscribes: (() => void)[] = []
        const load = async () => {
            const { configurationService, initialized } = state
            const updateRequired = !initialized || settingsChanged
            let appState: ICUIAppState = {
                configurationService,
                settings: {
                    theme: props.theme,
                    ...(await Promise.resolve(props.settings))
                } as IAppSettings
            }
            let error = null
            if (updateRequired) {
                try {
                    appState = await mergeDefaultSettings(appState)
                    appState = await loadTranslations(appState)
                    appState = await loadConfiguration(appState, cServices)
                } catch (e) {
                    error = e
                }
                registerAliases(appState)
                setState({
                    ...appState,
                    initialized: true,
                    visualizationEnabled: isVisualizationEnabled(
                        appState.settings?.visualization
                    ),
                    error
                })
                if (settingsChanged) {
                    setSettingsChanged(false)
                }
                props.eventBus.push({
                    type: Events.AppStateUpdated,
                    updatedAppState: appState
                })
            }
        }
        load()

        unsubscribes.push(
            props.eventBus.onValue((e) => {
                if (e === Events.ShowVisualization) {
                    setShowVisualization(true)
                } else if (e === Events.HideVisualization) {
                    setShowVisualization(false)
                }
            })
        )
        unsubscribes.push(
            props.eventBus.onValue((e) => {
                if (e === Events.HidePrices) {
                    setShowPrices(false)
                } else if (e === Events.ShowPrices) {
                    setShowPrices(true)
                }
            })
        )

        return () => unsubscribes.forEach((u) => u())
    }, [settingsChanged])

    useEffect(() => {
        busyOverlay.current?.setVisible(settingsChanged)
    }, [settingsChanged])

    const ComponentFactory = reactConfigurator.ComponentFactory
    const Configurator = reactConfigurator.Configurator

    if (!state.initialized) {
        return (
            <div className={classNames.loadingRoot}>
                {ComponentFactory.instanceOf(
                    ComponentName.InitialLoadingSpinner,
                    {
                        show: true,
                        label: new URLSearchParams(window.location.search).get(
                            "product"
                        )
                    }
                )}
            </div>
        )
    }
    if (state.error) {
        return (
            <Stack style={{ flex: 1 }}>
                <MessageBar
                    messageBarType={MessageBarType.error}
                    style={{ zIndex: 10000000000 }}
                >
                    {state.error.message ||
                        "Some error occurred. Please contact your administrator."}
                </MessageBar>
                <div className={classNames.loadingRoot}>
                    {ComponentFactory.instanceOf(
                        ComponentName.InitialLoadingSpinner,
                        {
                            show: true
                        }
                    )}
                </div>
            </Stack>
        )
    }

    if (!state.visualizationEnabled && showVisualization) {
        setShowVisualization(false)
    }
    const hiddenViews: string[] = []
    const resizableViews: string[] = []
    const borderLayoutChildren = []

    layout = prepareLayout(
        layout,
        state,
        showVisualization,
        hiddenViews,
        resizableViews
    )

    // Header
    borderLayoutChildren.push(
        <div
            key={BorderLayoutContent.Header}
            style={{ display: "flex", flexGrow: 1, minWidth: 0 }}
        >
            {ComponentFactory.instanceOf(ComponentName.Header, {
                eventBus: props.eventBus,
                configurationService: state.configurationService,
                visualizationEnabled: state.visualizationEnabled,
                showVisualization,
                showPrices
            })}
        </div>
    )

    // Configurator
    borderLayoutChildren.push(
        <div
            key={BorderLayoutContent.Configuration}
            style={{
                flex: 1,
                display: "flex",
                minWidth: 0,
                overflowY: "auto",
                overflowX: "hidden",
                position: "relative" // this is required to place the absolute positioned loading overlay only above the configurator
            }}
        >
            <Configurator
                lang={L10n.currentFullLocale}
                config={state.configurationService}
                options={{
                    showPrices,
                    appSettings: state.settings,
                    eventBus: props.eventBus,
                    readOnly: state.settings.configuration.options.readOnly
                }}
                eventBus={props.eventBus}
                onPending={onPending}
                onSavePoint={onSavePoint}
                ref={configuratorRef}
                appRefineProvider={props.appRefineProvider}
                styles={{
                    section: {
                        flex: 1,
                        display: "flex",
                        flexDirection: "row",
                        overflow: "hidden"
                    },
                    main: {
                        flex: 1,
                        display: "flex",
                        minWidth: 0,
                        overflowX: "auto",
                        overflowY: "hidden"
                    },
                    aside: {
                        display: "flex",
                        overflow: "auto"
                    }
                }}
            />
            {ComponentFactory.instanceOf(ComponentName.BusyOverlay, {
                initiallyHidden: true,
                componentRef: busyOverlay
            })}
        </div>
    )

    // Visualization
    borderLayoutChildren.push(
        <VisualizationWrapper
            key={BorderLayoutContent.VisualEditor}
            theme={theme}
            styles={styles}
            classNames={classNames}
            eventBus={props.eventBus}
            visualizationProvider={props.visualizationProvider}
            reactConfigurator={reactConfigurator}
            settings={state.settings.visualization}
            configurationSettings={state.settings.configuration}
            configurationService={state.configurationService}
            setBusyOverlay={onPending}
            visualEditorRef={visualEditorForwardRef}
        />
    )
    // Delegate children (custom components)
    if (props.children) {
        if (Array.isArray(props.children)) {
            props.children.forEach((child) => borderLayoutChildren.push(child))
        } else {
            borderLayoutChildren.push(props.children)
        }
    }

    return (
        <BorderLayoutComponent
            layout={layout}
            resize={resizableViews}
            hidden={hiddenViews}
            theme={theme}
            borderStyles={borderStyles}
            className={classNames.root}
        >
            {borderLayoutChildren}
        </BorderLayoutComponent>
    )
}

const ICUIAppWithVisualEditorForwardRef = React.forwardRef(
    (
        props: ICUIAppProps,
        visualEditorForwardRef: ForwardedRef<IVisualEditor>
    ) => ICUIApp(props, visualEditorForwardRef)
)
ICUIAppWithVisualEditorForwardRef.displayName = "CUIApp"

export const CUIApp = styled(ICUIAppWithVisualEditorForwardRef, CUIAppStyles)
